import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';
import Hls from 'hls.js';

import FormFields from '../../components/form/fields';
import ImageScroller from '../../components/imagescroller';
import HelpText from '../../components/gui/helpText';
import Input from '../../components/form/input';
import GuiList from '../../components/gui/list';
import UserProfileInline from '../../components/user/profileInline';
import Camera from '../../components/gui/camera';

import localUtil from '../../lib/util';
import { QRCodeSVG } from 'qrcode.react';
import Sparkline from '../../components/gui/sparkline';
import DateTileCard from '../../components/gui/dateTileCard';

function conventionImg(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3LargeLink}`;
    return imgSrc;
}

const MARKDOWN_OPTIONS = {
	pedantic: false,
	gfm: true,
	breaks: true,
	sanitize: false,
	smartLists: true,
	smartypants: true,
	xhtml: true,
    highlight: function(code, lang) {
        const hljs = require('highlight.js');
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
      },
    langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

class ConventionViewInfo extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            descriptionExpanded: false,
        };
    }

    toggleDescription = () => {
        this.setState(prev => ({ descriptionExpanded: !prev.descriptionExpanded }));
    }

    openStatsDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id) {
            return;
        }
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionStats', {
            height: drawerHeightLarge,
            conventionId: convention.id,
        }, drawerLevel + 2);
    }

    render() {
        // `part` splits the component so view.js can place the description card
        // below the signup card: 'info' = images, date tiles, facts and stats;
        // 'description' = the ingress/body card only.
        const { isStarted, countdown, part = 'info' } = this.props;
        const { appState, userStore, conventionStore, conventionResultStore } = this.props.stores;
        const { mainView, subView, isDevelopment, path } = appState;
        const { user = {}, isAdmin, isCustomerAdmin, isTester, isVeterinary, language, searchUsersResult } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        // `convention` defaults to `{}` because a not-found / inaccessible convention
        // loads as `undefined` (see the updateItem guard in stores/convention.js).
        const { convention = {}, jwtToken, otp = '' } = conventionStore;

        if (part === 'description') {
            if (!convention.ingress && !convention.body) {
                return null;
            }
            // Long texts are collapsed with a fade and a «Show more» link so the
            // page stays scannable; short texts are shown in full.
            const { descriptionExpanded } = this.state;
            const textLength = (convention.ingress || '').length + (convention.body || '').length;
            const isLong = textLength > 300;
            const collapsed = isLong && !descriptionExpanded;
            return (
                <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-2`}>
                    <div
                        class='text-wrap'
                        style={collapsed
                            ? 'max-height: 4.5rem; overflow: hidden; -webkit-mask-image: linear-gradient(to bottom, black 55%, transparent 100%); mask-image: linear-gradient(to bottom, black 55%, transparent 100%);'
                            : 'max-height: 60vh; overflow: auto;'}
                    >
                        {convention.ingress && (
                            <div class='mb-2'>
                                <Markdown markdown={convention.ingress} markedOpts={MARKDOWN_OPTIONS} />
                            </div>
                        )}
                        {convention.body && (
                            <Markdown markdown={convention.body} markedOpts={MARKDOWN_OPTIONS} />
                        )}
                    </div>
                    {isLong && (
                        <button
                            type='button'
                            class='btn btn-link btn-sm p-0 mt-1 text-decoration-none align-self-start'
                            onClick={this.toggleDescription}
                        >
                            {descriptionExpanded ? <>
                                <i class='fa-duotone fa-chevron-up me-1' />
                                <Text id='convention.show-less-description'>Show less</Text>
                            </> : <>
                                <i class='fa-duotone fa-chevron-down me-1' />
                                <Text id='convention.show-more-description'>Show more</Text>
                            </>}
                        </button>
                    )}
                </div>
            );
        }

        return (<>

            {isStarted ? <>
            </> : <>
                <div class='d-flex w-100 px-3'>
                    {convention.autoplayVideo && convention.files?.length > 0 && (() => {
                        const file = convention.files[0];
                        const poster = file.s3LinkStreamPreview1;
                        const hlsUrl = file.s3LinkStream;
                        if (hlsUrl) {
                            return (
                                <div class='video-player-wrapper d-flex flex-column w-100 rounded-lg overflow-hidden'>
                                    <video autoplay muted loop playsinline
                                        poster={poster} class='w-100'
                                        style='object-fit: cover;'
                                        ref={el => {
                                            if (el && !el._hlsAttached) {
                                                el._hlsAttached = true;
                                                if (Hls.isSupported()) {
                                                    const hls = new Hls();
                                                    hls.loadSource(hlsUrl);
                                                    hls.attachMedia(el);
                                                } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
                                                    el.src = hlsUrl;
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            );
                        }
                    })()}
                    {!(convention.autoplayVideo && convention.files?.some(f => f.s3LinkStream)) && (
                        <ImageScroller
                            images={convention.images}
                            stores={this.props.stores}
                            showImg={img => conventionImg(img, this.props, '1920x')}
                            showImageInfo={false}
                            classNamesWrapper1='rounded-lg overflow-hidden'
                            maxHeight='40vh'
                            imageContainerStyles='background: transparent;'
                        />
                    )}
                </div>
            </>}

            {/* Date & Time - same tile cards as the race detail page */}
            {convention.startDate && (
                <div class='d-flex flex-sm-row flex-column mx-2'>
                    <DateTileCard
                        stores={this.props.stores}
                        label={<Text id='convention.tile-start'>Start</Text>}
                        date={convention.startDate}
                        isCancelled={!!convention.isCancelled}
                        classesContainer='flex-fill'
                    />
                    {convention.endDate && (
                        <DateTileCard
                            stores={this.props.stores}
                            label={<Text id='convention.tile-end'>End</Text>}
                            date={convention.endDate}
                            isCancelled={!!convention.isCancelled}
                            classesContainer='flex-fill'
                        />
                    )}
                </div>
            )}

            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-1`}>
                {/* Location */}
                {convention.location && (
                    <div class='d-flex flex-row align-items-center mb-2'>
                        <i class='fa-duotone fa-location-dot text-danger me-3' style='font-size: 1.2em; width: 20px; text-align: center;' />
                        <span>{convention.location}</span>
                    </div>
                )}

                {/* Organizer - demoted to a small muted line so it doesn't compete with the title */}
                {convention.organizer && (
                    <div class='d-flex flex-row align-items-center mb-2'>
                        <i class='fa-duotone fa-users text-body-secondary me-3' style='width: 20px; text-align: center;' />
                        <small class='text-body-secondary'>{convention.organizer}</small>
                    </div>
                )}

                {/* Badge row - only cancelled / running are loud; the rest are quiet. Registration status is shown in the signup card below. */}
                <div class='d-flex flex-row flex-wrap align-items-center gap-2 mb-2 mt-1'>
                    {!!convention.isCancelled && (
                        <span class='badge text-bg-danger'><i class='fa-solid fa-ban me-1' />Avlyst</span>
                    )}
                    {isStarted && !convention.isCancelled && (
                        <span class='badge text-bg-primary'><i class='fa-duotone fa-circle-play me-1' />Pågår nå</span>
                    )}
                    {convention.type === 'dugnad' && (
                        <span class='badge text-bg-light' style='font-size: 0.7rem;'><i class='fa-duotone fa-people-carry-box me-1' /><Text id='convention.dugnad-badge'>Dugnad</Text></span>
                    )}
                    {convention.isCompetition && (
                        <span class='badge text-bg-light' style='font-size: 0.7rem;'><i class='fa-duotone fa-trophy me-1' />Konkurranse</span>
                    )}
                    {convention.isNsfClassLeading && (
                        <span class='badge text-bg-light' style='font-size: 0.7rem;'><i class='fa-duotone fa-medal me-1' />Klasseførende</span>
                    )}
                    {convention.selfSignup && (
                        <span class='badge text-bg-light' style='font-size: 0.7rem;'><i class='fa-duotone fa-user-plus me-1' />Selvregistrering</span>
                    )}
                    {convention.judges && (
                        <span class='badge text-bg-light' style='font-size: 0.7rem;'><i class='fa-duotone fa-gavel me-1' />{convention.judges}</span>
                    )}
                    {convention.nsfNumber > 0 && (
                        <small class='text-body-secondary' title='StevneID'>#{convention.nsfNumber}</small>
                    )}
                </div>
            </div>

            {/* Stats section - visible to admins and convention owners. Click opens detailed stats drawer. */}
            {(isAdmin || convention.user === user.id) && (convention.totalViews > 0 || convention.weeklyStats?.length > 0) && (
                <div
                    class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-2`}
                    style='cursor: pointer;'
                    role='button'
                    title='Show detailed statistics'
                    onClick={this.openStatsDrawer}
                >
                    <div class='d-flex flex-row align-items-center flex-wrap'>
                        <span class='text-muted font-weight-light me-2'>
                            <i class='fa-solid fa-chart-simple' /> Stats
                        </span>
                        <span class='badge text-bg-light me-2' title='Total views'>
                            <i class='fa-solid fa-eye' /> {convention.totalViews}
                            {convention.viewsTrend !== 0 && (
                                <span class={`ms-1 ${convention.viewsTrend > 0 ? 'text-success' : 'text-danger'}`}>
                                    {convention.viewsTrend > 0 ? '+' : ''}{convention.viewsTrend}%
                                </span>
                            )}
                        </span>
                        {convention.totalSignups > 0 && (
                            <span class='badge text-bg-light me-2' title='Total signups'>
                                <i class='fa-solid fa-user-plus' /> {convention.totalSignups}
                            </span>
                        )}
                        {convention.totalResultsEntered > 0 && (
                            <span class='badge text-bg-light me-2' title='Results entered'>
                                <i class='fa-solid fa-trophy' /> {convention.totalResultsEntered}
                            </span>
                        )}
                        {convention.totalResultViews > 0 && (
                            <span class='badge text-bg-light me-2' title='Result views'>
                                <i class='fa-solid fa-list' /> {convention.totalResultViews}
                            </span>
                        )}
                        <span class='text-muted ms-auto'>
                            <i class='fa-solid fa-chevron-right' />
                        </span>
                    </div>
                    {convention.weeklyStats?.length > 1 && (
                        <div class='d-flex flex-row align-items-center mt-2'>
                            <span class='text-muted font-weight-light me-2' style='font-size: 0.85em;'>
                                Weekly trend:
                            </span>
                            <Sparkline
                                data={convention.weeklyStats}
                                valueField='views'
                                height={24}
                                barWidth={6}
                                barGap={2}
                                maxBars={12}
                            />
                        </div>
                    )}
                </div>
            )}
        </>);
    }
}
export default ConventionViewInfo;
